import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ITargetGroup } from '../../../entities/target-group/domain/target-group.types';
import { getUserRoles, hasSomeRoles, UserRole } from '../../../entities/user/domain/user.types';
import { userSelector } from '../../../entities/user/store/user.selectors';
import { getTargetGroupEditStatus } from '../helpers/target-group-edit-time';

/**
 * Custom hook for handling the target group edit time window functionality
 *
 * @returns An object containing the edit status and a refresh function
 */
export const useTargetGroupEditTime = (targetGroup: ITargetGroup) => {
  const user = useSelector(userSelector);
  const roles = getUserRoles(user);
  const isAdmin = hasSomeRoles([UserRole.REVI_EMPLOYEE], roles) || false;

  const [editStatus, setEditStatus] = useState(() =>
    getTargetGroupEditStatus(targetGroup, isAdmin),
  );

  const refreshEditStatus = useCallback(() => {
    setEditStatus(getTargetGroupEditStatus(targetGroup, isAdmin));
  }, [targetGroup, isAdmin]);

  useEffect(() => {
    refreshEditStatus();

    if (editStatus.isNewTargetGroup) {
      return;
    }

    const timer = setInterval(() => {
      refreshEditStatus();
    }, 60000);

    return () => clearInterval(timer);
  }, [targetGroup, refreshEditStatus]);

  return {
    ...editStatus,
    refreshEditStatus,
    isAdmin,
  };
};

export default useTargetGroupEditTime;
